import { useEffect, useMemo, useState } from 'react'
import { api, type VerificationDetail as Detail } from '../lib/api.ts'
import { OutdatedEvaluationNotice } from '../components/OutdatedEvaluationNotice.tsx'
import { useLateErp } from '../lib/useLateErp.ts'
import { GoldenCaseScore } from '../components/GoldenCaseScore.tsx'
import { PrescriptionViewer } from '../components/PrescriptionViewer.tsx'
import { usePrescriptionPages } from '../lib/usePrescriptionPages.ts'
import { prescriptionFileBase } from '../lib/prescriptionDocument.ts'
import { useManualAttachments, shownPages } from '../lib/useManualAttachments.ts'
import {
  AttachReceitaButton,
  ManualAttachmentError,
  ManualReceitaMark,
} from '../components/ManualAttachments.tsx'
import { Progress } from '../components/Progress.tsx'
import { ComparisonCard } from '../components/ComparisonCard.tsx'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import { Tag } from '../components/Tag.tsx'
import { CardHeader, PANEL } from '../components/CardHeader.tsx'
import { countsFor, groupByItem, hasIssues, parseSide } from '../lib/verificationItems.ts'
import { isAnalysing } from '../lib/vocabulary.ts'
import { useVerificationProgress, withProgress } from '../lib/useVerificationProgress.ts'
import { StatusBadge } from '../components/StatusBadge.tsx'
import { titleCaseName } from '../lib/titleCase.ts'

/**
 * One verification: the prescription beside what was keyed, with every finding
 * traceable to a rectangle on the original photograph.
 *
 * The shape is V1's, because the shape is the argument. Two titled cards --
 * Receita and Comparação -- and inside the second one the document's own
 * checks, then **one preparation at a time**, each with its checks as two
 * columns, RECEITA against NO SISTEMA. What V2 had was a flat list of every
 * finding grouped by check name, which asks a pharmacist to assemble the
 * picture themselves, once per finding.
 *
 * Selecting a row highlights **exactly its blocks** — the dose, not the whole
 * ingredient line — and the viewer frames them. That precision is the entire
 * reason citations were recorded per field rather than per item.
 */
export function VerificationDetail({
  id,
  onBack,
  onOpenCase,
}: {
  id: string
  onBack: () => void
  /** Back to the golden-set case this was run from, when it was run from one. */
  onOpenCase?: ((caseId: string) => void) | undefined
}) {
  const [detail, setDetail] = useState<Detail | undefined>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [notice, setNotice] = useState<string | undefined>()
  const [viewers, setViewers] = useState<string[]>([])
  // Quiet rows are counted in the header and listed only on request, as V1
  // does: walking somebody past fourteen agreements to reach two questions is
  // how a screen teaches people to scroll past it.
  const [showClear, setShowClear] = useState(false)
  // Before the early returns below, as every hook must be. Reads the run's
  // status off whatever detail is here; nothing to read is "not analysing".
  const manual = useManualAttachments(id, () => void load())
  // Which document the viewer has on screen, for the header's manual mark.
  const [shownAttachment, setShownAttachment] = useState<string | undefined>()
  const { pages: listedPages, status: pagesStatus } = usePrescriptionPages(
    id,
    isAnalysing(detail?.live?.status ?? detail?.verification.status ?? ''),
    manual.revision,
  )
  // A removed receita leaves the screen now, and an attached one shows now,
  // not when the run that follows ends.
  const pageUrls = useMemo(
    () =>
      shownPages(listedPages, {
        attachments: manual.attachments,
        loaded: manual.loaded,
        previews: manual.previews,
      }),
    [listedPages, manual.attachments, manual.loaded, manual.previews],
  )

  async function load() {
    try {
      const next = await api.verification(id)
      setDetail(next)
      setError(undefined)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao carregar a conferência')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  /*
   * The checkpoint, while there is one to watch.
   *
   * This screen used to read once on mount and never again, so a conferência
   * opened while it was being analysed sat on whatever checkpoint it had at
   * that instant -- "buscando orçamento", minutes after the orçamento was
   * found -- until somebody pressed Atualizar. The strip is the one thing
   * here that goes stale on its own, and now the one thing that refreshes.
   */
  useVerificationProgress(
    id,
    isAnalysing(detail?.verification.status ?? ''),
    (progress) =>
      setDetail((current) => (current === undefined ? current : withProgress(current, progress))),
    () => void load(),
  )

  // A pote (#558) or a label (#614) the ERP wrote after this was read: said,
  // with what to do about it a press away.
  useLateErp(
    id,
    () => void reverify(),
    () => void load(),
  )

  useEffect(() => {
    // Announce and read back, on a beat comfortably shorter than the server's
    // window so a viewer does not flicker out between calls.
    let live = true
    const beat = () => {
      void api.presence(id).then((who) => {
        if (live) setViewers(who)
      })
    }
    beat()
    const timer = setInterval(beat, 20_000)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [id])

  const decisions = useMemo(
    () => new Map((detail?.decisions ?? []).map((r) => [r.fingerprint, r])),
    [detail],
  )

  const answered = useMemo(() => new Set(decisions.keys()), [decisions])

  const prescription = useMemo(() => parseSide(detail?.verification.prescription), [detail])
  const quotation = useMemo(() => parseSide(detail?.verification.quotation), [detail])

  const grouped = useMemo(
    () => groupByItem(quotation, detail?.findings ?? [], answered, prescription),
    [quotation, prescription, detail, answered],
  )

  /*
   * No highlight in this panel, following V1.
   *
   * Its "ver na receita" button died in a review round and the region
   * highlight died with it -- the mechanism that survived is the guided pass,
   * where the rectangle follows the step without anybody asking for it. A
   * highlight here would need a way to choose one finding out of the whole
   * screen, which is the work the guided pass exists to remove.
   */

  async function record(
    fingerprint: string,
    decision: string,
    options?: {
      synonymTerm?: string | undefined
      notes?: string | undefined
      misread?: boolean | undefined
    },
  ) {
    setBusy(true)
    setNotice(undefined)
    try {
      const result = await api.recordDecision(id, fingerprint, decision, options)
      if (result.synonym === 'proposed' || result.synonym === 'counted') {
        setNotice('Sinônimo enviado para aprovação. Nada muda até alguém aprovar.')
      }
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao registrar')
    } finally {
      setBusy(false)
    }
  }

  /** Withdraws a decision, so a finding somebody answered comes back. */
  async function clearDecision(fingerprint: string) {
    setBusy(true)
    setNotice(undefined)
    try {
      await api.clearDecision(id, fingerprint)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao desfazer')
    } finally {
      setBusy(false)
    }
  }

  async function reverify() {
    setBusy(true)
    setError(undefined)
    try {
      await api.reverify(id)
      setNotice('Reconferência solicitada. Os achados que não mudaram mantêm sua decisão.')
      // Read again, so the screen shows the run it just asked for: the status
      // it held said `awaiting_verifier`, and everything that offers a reverify
      // -- the outdated notice among them -- would go on offering it (#605).
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao solicitar reconferência')
    } finally {
      setBusy(false)
    }
  }

  if (error !== undefined && detail === undefined) {
    // Two different situations that a single "erro ao carregar" would merge.
    // One is the pharmacy's system being unreachable, which nobody here can
    // fix and which resolves itself; the other is ours.
    const erpUnavailable = /erp|firebird|unreachable|timeout/i.test(error)
    return (
      <div className="flex flex-col items-start gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Voltar para a fila
        </Button>
        {erpUnavailable ? (
          <>
            <h1 className="text-tH4 text-text-strong">Sistema da farmácia indisponível</h1>
            <p className="text-tP text-text-weak">
              Não foi possível ler o orçamento no ERP. A conferência continua na fila e será
              retomada automaticamente.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-tH4 text-text-strong">Não foi possível carregar</h1>
            <p className="text-tP text-text-error">{error}</p>
            <Button variant="secondary" tone="brand" size="small" onClick={() => void load()}>
              Tentar de novo
            </Button>
          </>
        )}
      </div>
    )
  }

  if (detail === undefined) {
    return (
      <p className="text-tP text-text-weak" role="status" aria-live="polite">
        Carregando…
      </p>
    )
  }

  const counts = countsFor(detail.findings, answered)

  /**
   * Still working its way to a conference.
   *
   * Read from the step rather than from an empty findings list, because
   * "nothing found yet" and "nothing wrong" render identically and mean
   * opposite things.
   */
  /*
   * The checkpoint, and separately the status. Never one standing in for the
   * other.
   *
   * This fell back `step ?? status`, so a run with no checkpoint handed a
   * status to a strip that labels checkpoints: `waiting` and `concluding`
   * printed as raw keys in the meta line, uncoloured, next to a badge already
   * saying the same thing properly.
   *
   * The workflow first, because it knows before the row does; the row after,
   * because it is what remains once the workflow is gone.
   */
  const step = detail.live?.step ?? detail.verification.step ?? undefined
  const status = detail.live?.status ?? detail.verification.status
  const analysing = isAnalysing(status)

  const itemsWithIssues = grouped.items.filter((item) => hasIssues(item.findings))
  const clearItems = grouped.items.filter((item) => !hasIssues(item.findings))
  const visibleDocument = showClear
    ? grouped.document
    : grouped.document.filter((f) => hasIssues([f]))

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/*
        How this reading compared to the answer key, when there is one.
        
        Only for an verification dispatched from a golden-set case, which is a
        super-admin's simulation and never a pharmacist's work -- so this
        never appears on a real verification.
      */}
      {detail.goldenCase !== undefined && (
        <GoldenCaseScore source={detail.goldenCase} onOpenCase={onOpenCase} />
      )}

      {/* The patient's name is the heading, with the numbers as meta beneath
          it. A pharmacist knows which verification they are in by whose it is. */}
      <div className="flex flex-row items-center gap-5">
        <Button
          size="icon"
          variant="secondary"
          tone="brand"
          aria-label="Voltar para a fila"
          leadingIcon={icons.back}
          onClick={onBack}
        />
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="text-tH4 text-text-strong font-medium">
            {titleCaseName(prescription?.patientName) || 'Paciente não identificado'}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <Meta>Orçamento {detail.verification.quotation_number}</Meta>
            <MetaDot />
            <Meta>Filial {detail.verification.branch_code}</Meta>
            <MetaDot />
            {/* A badge, like the queue's -- the same fact drawn the same way,
                because a person moving between the two screens is following
                one thing and two renderings of it read as two things. */}
            <StatusBadge status={status} reason={detail?.verification.failure_reason} />
            {viewers.length > 0 && (
              <>
                <MetaDot />
                {/* So two pharmacists do not unknowingly work the same verification
                    and reach different decisions minutes apart. */}
                <Meta title={viewers.join(', ')}>
                  {viewers.length === 1
                    ? `${viewers[0]} também está aqui`
                    : `${viewers.length} pessoas também estão aqui`}
                </Meta>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Gated on the status as well as the server's word, because the workflow
          knows a run has started before the row the verdict was read from does. */}
      {detail.outdatedEvaluation !== undefined && status === 'awaiting_verifier' && (
        <OutdatedEvaluationNotice
          outdated={detail.outdatedEvaluation}
          busy={busy}
          onReverify={() => void reverify()}
        />
      )}

      {notice !== undefined && <p className="text-tP text-text-success">{notice}</p>}
      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      <div className="flex items-stretch gap-4 max-lg:flex-col">
        {/* ---- Receita ---- */}
        <section className="rounded-base border-stroke-weak bg-background-raised shadow-raised flex flex-1 flex-col border">
          <CardHeader
            {...PANEL.prescription}
            beside={
              <ManualReceitaMark
                manual={manual}
                shownAttachmentId={shownAttachment}
                disabled={analysing || status === 'released'}
              />
            }
          >
            <AttachReceitaButton manual={manual} disabled={analysing || status === 'released'} />
          </CardHeader>
          <div className="flex flex-col gap-3 p-5">
            <ManualAttachmentError manual={manual} />
            <PrescriptionViewer
              onShownChange={setShownAttachment}
              bringForward={manual.latest}
              pageUrls={pageUrls}
              pagesStatus={pagesStatus}
              fileBase={prescriptionFileBase(detail.verification)}
              blocks={detail.blocks}
              highlighted={[]}
            />
          </div>
        </section>

        {/* ---- Comparação ---- */}
        <section className="rounded-base border-stroke-weak bg-background-raised shadow-raised flex min-h-full flex-1 flex-col border">
          <CardHeader {...PANEL.comparison}>
            <Button variant="primary" tone="brand" size="small" disabled={busy} onClick={reverify}>
              {busy ? 'Atualizando…' : 'Atualizar comparação'}
            </Button>
          </CardHeader>

          <div className="border-stroke-weak bg-background-sunken flex flex-col gap-3 border-b p-5">
            {counts.flagged > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag tone="danger" variant="filled">
                  {counts.flagged === 1 ? '1 divergência' : `${counts.flagged} divergências`}
                </Tag>
              </div>
            )}

            {/* The quiet rows are counted, not listed. Fourteen "this was
                fine" panels between a pharmacist and the two that matter is
                how a screen teaches people to scroll past it. */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-tP text-text-weak">
                {counts.total - counts.flagged === 1
                  ? '1 verificação sem divergência'
                  : `${counts.total - counts.flagged} verificações sem divergência`}
              </span>
              <MetaDot />
              <button
                type="button"
                className="text-tP text-text-brand cursor-pointer border-none bg-transparent p-0"
                onClick={() => setShowClear((v) => !v)}
              >
                {showClear ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>

          <div className="flex flex-col p-5">
            {analysing && step !== undefined && (
              /* A simulation is watched from the moment it is dispatched, and an
                 empty findings list looks identical to a finished verification with
                 nothing wrong. */
              <div className="mb-8">
                <Progress step={step} />
              </div>
            )}

            {visibleDocument.length > 0 && (
              <div className="mb-12 flex flex-col">
                <span className="text-tP text-text-weak mb-6">Identidade</span>
                <ComparisonCard
                  title="Paciente e prescritor"
                  findings={visibleDocument}
                  decisions={decisions}
                  busy={busy}
                  onDecision={record}
                  onClearDecision={clearDecision}
                />
              </div>
            )}

            {(itemsWithIssues.length > 0 || (showClear && clearItems.length > 0)) && (
              <>
                <span className="text-tP text-text-weak mb-6">Fórmulas da receita</span>
                <div className="flex flex-col gap-6">
                  {[...itemsWithIssues, ...(showClear ? clearItems : [])].map((item) => (
                    <ComparisonCard
                      key={item.preparationId}
                      title={`${item.ordinal}) ${item.title}`}
                      findings={item.findings}
                      item={item}
                      decisions={decisions}
                      busy={busy}
                      onDecision={record}
                      onClearDecision={clearDecision}
                    />
                  ))}
                </div>
              </>
            )}

            {grouped.orphans.length > 0 && (
              /* Never expected. Always shown: an item that exists must never
                 vanish from this screen, whatever the grouping thinks. */
              <div className="mt-12 flex flex-col">
                <span className="text-tP text-text-weak mb-6">Itens não pareados</span>
                <ComparisonCard
                  title="Sem preparação correspondente"
                  findings={grouped.orphans}
                  decisions={decisions}
                  busy={busy}
                  onDecision={record}
                  onClearDecision={clearDecision}
                />
              </div>
            )}

            {detail.findings.length === 0 && !analysing && (
              <p className="text-tP text-text-weak">Nenhum achado registrado ainda.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function Meta({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span className="text-tP text-text-weak" title={title}>
      {children}
    </span>
  )
}

function MetaDot() {
  return (
    <span aria-hidden="true" className="bg-text-weak inline-block h-1 w-1 shrink-0 rounded-full" />
  )
}
